import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from './utils/axiosConfig';
import Lightbox from 'react-image-lightbox'; // 匯入 Lightbox
import 'react-image-lightbox/style.css'; // 匯入 Lightbox 的樣式
import { Form, Container, Row, Col } from 'react-bootstrap';

// 防抖自定義 Hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};


const LinePhotoWall = () => {
  const [employees, setEmployees] = useState([]);
  const [storeNames, setStoreNames] = useState([]); // 儲存所有 store_name 的列表
  const [selectedStore, setSelectedStore] = useState(''); // 儲存選中的 store_name
  const [searchQuery, setSearchQuery] = useState(''); // 新增搜尋查詢狀態
  const [page, setPage] = useState(1); // 初始頁碼為 1
  const [hasMore, setHasMore] = useState(true); // 是否還有更多資料
  const [isLightboxOpen, setIsLightboxOpen] = useState(false); // 控制 Lightbox 開啟狀態
  const [lightboxImage, setLightboxImage] = useState(''); // Lightbox 顯示的圖片
  const [loading, setLoading] = useState(false); // 載入狀態
  const token = localStorage.getItem('token');
  const debouncedSearch = useDebounce(searchQuery, 300); // 防抖搜尋

  // 獲取所有 store_name 用於下拉選單
  const fetchStoreNames = async () => {
    try {
      const response = await axios.get('/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setStoreNames(response.data); // 假設 response.data 是 store_name 的數組
    } catch (error) {
      // Error fetching store names
    }
  };

  const fetchEmployees = useCallback(async (currentPage, storeFilter) => {
    setLoading(true);
    try {
      const response = await axios.get('/Allemployees', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { 
          page: currentPage, 
          store_name: storeFilter,
          filterWithPicture: 'true', // 只獲取有照片的員工
          allData: 'true' // 獲取所有數據以便前端搜尋
        },
      });
      
      // 過濾有 e_photo 的員工
      const employeesWithPhotos = (response.data.data || []).filter(employee => employee.e_photo);

      // 更新員工資料
      if (currentPage === 1) {
        setEmployees(employeesWithPhotos);
      } else {
        setEmployees(prevEmployees => [...prevEmployees, ...employeesWithPhotos]);
      }
      
      setHasMore(response.data.hasMore || false);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 初始加載 store_name 列表
  useEffect(() => {
    fetchStoreNames();
  }, []);

  // 初始加載和每次頁碼或篩選條件變化時加載員工資料
  useEffect(() => {
    fetchEmployees(page, selectedStore);
  }, [page, selectedStore]);

  // 處理滾動事件以加載更多
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 50 && hasMore) {
        setPage(prevPage => prevPage + 1); // 加載下一頁
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore]);

  // 處理 store_name 下拉選單變更
  const handleStoreChange = (e) => {
    setSelectedStore(e.target.value);
    setPage(1); // 重置頁碼
    setEmployees([]); // 清空已加載的員工
  };

  // 處理搜尋輸入變更
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // 過濾後的員工資料（基於搜尋和社別篩選）
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const matchesSearch = !debouncedSearch || 
        (employee.e_name && employee.e_name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (employee.position_title && employee.position_title.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (employee.store_name && employee.store_name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (employee.sql_title4 && employee.sql_title4.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
        (employee.e_comp_name && employee.e_comp_name.toLowerCase().includes(debouncedSearch.toLowerCase()));
      
      const matchesStore = !selectedStore || employee.store_name === selectedStore;
      
      return matchesSearch && matchesStore;
    });
  }, [employees, debouncedSearch, selectedStore]);

  // 處理 Lightbox 圖片點擊
  const handleImageClick = useCallback((imageSrc) => {
    setLightboxImage(imageSrc);
    setIsLightboxOpen(true);
  }, []);

  return (
    <Container fluid style={{ 
      padding: '20px',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      {/* 標題區域 */}
      <Row className="mb-4">
        <Col xs={12} className="text-center">
          <h2 style={{ 
            color: '#2c3e50', 
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
          }}>
            社友相片錄
          </h2>
        </Col>
      </Row>

      {/* 篩選控制區域 */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label style={{ fontWeight: 'bold', color: '#34495e' }}>選擇社別:</Form.Label>
            <Form.Select
              value={selectedStore}
              onChange={handleStoreChange}
              style={{
                borderRadius: '10px',
                border: '2px solid #bdc3c7',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              <option value="">全部社別</option>
              {storeNames.map((store) => (
                <option key={store.ss_id} value={store.ss_name}>{store.ss_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        
        <Col md={6} className="mb-3">
          <Form.Group>
            <Form.Label style={{ fontWeight: 'bold', color: '#34495e' }}>搜尋社友+媒合找尋:</Form.Label>
            <Form.Control
              type="text"
              placeholder="搜尋姓名、職位、社別、行業關鍵字或公司名稱..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                borderRadius: '10px',
                border: '2px solid #bdc3c7',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* 統計資訊 */}
      <Row className="mb-3">
        <Col xs={12} className="text-center">
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.8)',
            borderRadius: '15px',
            padding: '10px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
          }}>
            <span style={{ fontSize: '14px', color: '#7f8c8d' }}>
              顯示 {filteredEmployees.length} 位社友的相片
              {selectedStore && ` (${selectedStore})`}
              {debouncedSearch && ` | 媒合搜尋: "${debouncedSearch}"`}
            </span>
          </div>
        </Col>
      </Row>

      {/* 員工照片牆 */}
      <Row>
        <Col xs={12}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            gap: '20px',
            textAlign: 'center',
            padding: '10px'
          }}>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee, index) => (
                <div key={`${employee.e_id}-${index}`} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  lineHeight: '1.2',
                  margin: 0,
                  padding: 0,
                  transition: 'transform 0.2s ease-in-out',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <img
                    src={`/uploads/member/${employee.e_photo}?t=${new Date().getTime()}`}
                    alt={employee.e_name}
                    style={{
                      width: '60px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '4px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                    onClick={() => handleImageClick(`/uploads/member/${employee.e_photo}?t=${new Date().getTime()}`)}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '12px' }}>{employee.store_name}</p>
                  <p style={{ margin: 0, fontWeight: 'bold', fontSize: '11px' }}>{employee.position_title}</p>
                  <p style={{ margin: 0, fontSize: '11px' }}>{employee.e_name}</p>
                </div>
              ))
            ) : !loading && (
              <div style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '40px',
                color: '#7f8c8d',
                fontSize: '18px'
              }}>
                {debouncedSearch || selectedStore ? '沒有找到符合媒合條件的社友' : '目前沒有社友上傳相片'}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* 載入指示器 */}
      {loading && (
        <Row className="mb-4">
          <Col xs={12} className="text-center">
            <div style={{ marginTop: '10px', color: '#7f8c8d' }}>載入中...</div>
          </Col>
        </Row>
      )}

      {/* Lightbox 顯示區域 */}
      {isLightboxOpen && (
        <Lightbox
          mainSrc={lightboxImage}
          onCloseRequest={() => setIsLightboxOpen(false)}
        />
      )}
    </Container>
  );
};

export default LinePhotoWall;
